import { Game } from '../typings/games';
import axios, { AxiosResponse } from 'axios';
import store from '../store';
import { bench } from '../utils/bench';

const CACHE_KEY = 'pala.games.cache.v1';

let inFlight: Promise<Game[]> | null = null;

export function getGames(token: string): Promise<Game[]> {
  if (store.state.games !== null) {
    return Promise.resolve(store.state.games);
  }

  if (inFlight !== null) {
    return inFlight;
  }

  const cached = readCache();
  if (cached !== null && cached.length > 0) {
    store.commit('setGames', cached);
    fetchGames(token).catch(() => undefined);
    return Promise.resolve(cached);
  }

  inFlight = fetchGames(token).finally(() => {
    inFlight = null;
  });
  return inFlight;
}

export function refreshGames(token: string): Promise<Game[]> {
  if (inFlight !== null) {
    return inFlight;
  }

  inFlight = fetchGames(token).finally(() => {
    inFlight = null;
  });
  return inFlight;
}

function readCache(): Game[] | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : null;
  } catch (e) {
    return null;
  }
}

function writeCache(games: Game[]): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(games));
  } catch (e) {
    // ignore
  }
}

async function fetchGames(token: string): Promise<Game[]> {
  let raw: AxiosResponse;
  let t = performance.now();
  try {
    raw = await axios({
      url: 'https://launcher.cef.paladium.games/api/games/listGames',
      method: 'post',
      headers: {
        'Content-Type': 'application/json;charset=UTF-8',
        'authorization': "Bearer " + token,
      },
    });
  } catch (e: any) {
    console.error(e);
    throw new Error('Impossible de récupérer la liste des jeux');
  }
  bench('games/listGames', performance.now() - t);

  if (raw.status !== 200) {
    throw new Error('listGames status ' + raw.status);
  }

  const glist: string[] = raw.data;
  t = performance.now();
  const settled = await Promise.allSettled(glist.map(g => getGame(g, token)));
  bench(`games/getGame x${glist.length}`, performance.now() - t);

  const results: Game[] = new Array<Game>();
  for (const outcome of settled) {
    if (outcome.status === 'fulfilled' && outcome.value) {
      results.push(outcome.value);
    }
  }

  if (results.length > 0) {
    store.commit('setGames', results);
    writeCache(results);
  }
  return results;
}

export async function getGame(id: string, token: string): Promise<Game | null> {
  try {
    const raw: AxiosResponse = await axios({
      url: 'https://launcher.cef.paladium.games/api/games/getGame',
      method: 'post',
      headers: {
        'Content-Type': 'application/json;charset=UTF-8',
        'authorization': "Bearer " + token,
      },
      data: JSON.stringify({ gameId: id }),
    });

    if (raw.status === 200) {
      return raw.data;
    }
    return null;
  } catch (e: any) {
    console.error(e);
    return null;
  }
}
